import React, { memo, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import { Stack, Typography, useMediaQuery } from "@mui/material";

import Panel from "./Panel";
import { DecimalInput } from "../../../../components/DecimalInput";
import Tooltip from "../../../../components/Tooltip";
import OffersSummary from "../Offers/OffersSummary";

import {
  useAppSelector,
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store/hooks";
import {
  selectAlerts,
  selectAverageData,
  selectOffers,
  selectProfitCalculator,
  selectQuickInfo,
} from "../../../../features/productAnalyzer/productAnalyzer.selector";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  isNumber,
  toNumber,
} from "../../../../helpers/formatter";
import Calculator from "../../../../helpers/profitCalculator";
import { selectProductAnalyzerSettings } from "../../../../features/settings/settings.selector";

import { useStyles } from "./index.styles";
import { updateLookupThunk } from "../../../../features/productAnalyzer/productAnalyzer.thunk";
// import { selectIsFreePlan } from "../../../../features/pricingPlans/pricingPlans.selector";
import useProductAnalyzerParams from "../../../../hooks/useProductAnalyzerParams";
import { selectAmazonAccount } from "../../../../features/user/user.selector";

interface IAlertProps {
  variant?: "success" | "danger" | "warning";
  messages: string[];
}

const COLORS = {
  success: "#047647",
  warning: "#E8A217",
  danger: "#A94442",
};

const FONT_COLORS = {
  success: "#E9FBEF",
  warning: "#FFF4D9",
  danger: "#F2DEDE",
};

const getAlertAbbr = (msg: string = "") => {
  if (msg.includes("Only one seller has had BB")) {
    return "BB";
  } else if (msg.includes("Private Label")) {
    return "PL";
  } else if (msg.includes("IP Analysis")) {
    return "IP";
  } else if (msg.includes("Oversize - Small") || msg.includes("Over Size")) {
    return "S";
  } else {
    return msg.slice(0, 1).toUpperCase();
  }
};

type TAveragePricingPeriod = "C" | "30" | "90" | "180" | "All";

export const getBsrDisplay = (bsr: number, catProductCount?: number) => {
  // let msg = bsr.toLocaleString();
  let msg = formatNumber(bsr, { forcePlusSuffix: true });
  if (catProductCount && catProductCount > 0) {
    msg += ` (${Math.ceil((bsr / catProductCount) * 100)}%)`;
  }
  return msg;
};

const Alert: React.FunctionComponent<IAlertProps> = ({
  messages,
  variant = "success",
}) => {
  const classes = useStyles();
  return (
    <Tooltip
      title={
        <>
          {messages.map((msg, index) => (
            <Box key={index}>{msg}</Box>
          ))}
        </>
      }
      arrow
      classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
    >
      <Box
        sx={{
          borderRadius: "6px",
          width: "22px",
          height: "22px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderColor: COLORS[variant],
          backgroundColor: COLORS[variant],
        }}
      >
        <Typography color={FONT_COLORS[variant]}>
          {messages.length > 1 ? messages.length : getAlertAbbr(messages[0])}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const QuickInfo = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { asin, lookupId } = useProductAnalyzerParams();
  const { quickInfo } = useSelector(selectQuickInfo);
  const { profitCalculator, feeRules } = useSelector(selectProfitCalculator);
  const { alerts, loadingAlerts } = useAppSelector(selectAlerts);
  const { averages, loadingAverages } = useAppSelector(selectAverageData);
  const isSmallScreen = useMediaQuery("(max-width:419px)");
  // const isFreePlan = useAppSelector(selectIsFreePlan);
  const { isConnected } = useAppSelector(selectAmazonAccount);
  const {
    minBsr,
    maxBsr,
    showBreakevenInQuickInfo,
    showOfferSummaryInQuickInfo,
    showProfitInQuickInfo,
    showProfitMarginInQuickInfo,
    showRoiInQuickInfo,
    showAveragePricingInQuickInfo,
    defaultAverageTimeFrameInQuickInfo
  } = useAppSelector(selectProductAnalyzerSettings);
  const { offers, loadingOffers } = useAppSelector(selectOffers);

  const hasAmazonOffer = useMemo(
    () =>
      offers.some((offer) => offer.sellerName.toLowerCase() === "amazon.com"),
    [offers],
  );

  const fbaCount = useMemo(
    () => offers.filter((offer) => offer.fulfillment === "FBA").length,
    [offers],
  );

  const fbmCount = useMemo(
    () => offers.filter((offer) => offer.fulfillment === "FBM").length,
    [offers],
  );
  const [averagePricingPeriod, setAveragePricingPeriod] =
    useState<TAveragePricingPeriod>(
      (defaultAverageTimeFrameInQuickInfo === "Current" 
        ? "C" 
        : defaultAverageTimeFrameInQuickInfo === "Days30"
          ? "30"
          : defaultAverageTimeFrameInQuickInfo === "Days90"
            ? "90"
            : defaultAverageTimeFrameInQuickInfo === "Days180"
              ? "180"
              : defaultAverageTimeFrameInQuickInfo === "All"
                ? "All"
                : "C") as TAveragePricingPeriod,
    );

  const handleCostChange = (costPrice: number) => {
    dispatch(updateLookupThunk({ costPrice }));
  };

  const handlePriceChange = (salePrice: number) => {
    dispatch(updateLookupThunk({ salePrice }));
  };

  if (!profitCalculator || !quickInfo || !feeRules) return null;

  const { profit, margin, roi, maxCost, breakevenSalePrice } = new Calculator(
    profitCalculator,
    feeRules,
  ).calculate();

  const costPrice: number = toNumber(profitCalculator.costPrice);
  const salePrice: number = toNumber(profitCalculator.salePrice);
  const bsr: number = isNumber(quickInfo.bsr) ? Number(quickInfo.bsr) : -1;
  const categoryProductCount: number = toNumber(quickInfo.categoryProductCount);
  const bsrPercent: number = Math.ceil((bsr / categoryProductCount) * 100);

  // Calculate profit and ROI for a given sale price
  const calculateProfitAndRoi = (salePriceValue: number) => {
    if (salePriceValue == null || !profitCalculator || !feeRules) {
      return { profit: 0, roi: 0 };
    }
    // Calculate even if value is 0
    const calculator = new Calculator(
      {
        ...profitCalculator,
        salePrice: salePriceValue,
      },
      feeRules,
    );
    const { profit, roi } = calculator.calculate();
    return { profit: toNumber(profit), roi: toNumber(roi) };
  };

  const getAveragePriceColor = (value?: number | null) => {
    // Treat null/undefined as 0 for calculation
    const numericValue = toNumber(value ?? 0);
    // Calculate profit and ROI for this average price (treating it as sale price)
    const { profit: calculatedProfit, roi: calculatedRoi } =
      calculateProfitAndRoi(numericValue);

    // Compare with minimum profit and ROI settings
    const minProfit = toNumber(profitCalculator.minProfit);
    const minRoi = toNumber(profitCalculator.minRoi);
    const meetsCriteria =
      calculatedProfit >= minProfit && calculatedRoi >= minRoi;

    return meetsCriteria ? "#047647" : "#A94442";
  };

  const getAveragePriceBoxStyle = (value?: number | null) => {
    // Treat null/undefined as 0 for calculation
    const numericValue = toNumber(value ?? 0);
    // Calculate profit and ROI for this average price (treating it as sale price)
    const { profit: calculatedProfit, roi: calculatedRoi } =
      calculateProfitAndRoi(numericValue);

    // Compare with minimum profit and ROI settings
    const minProfit = toNumber(profitCalculator.minProfit);
    const minRoi = toNumber(profitCalculator.minRoi);
    const meetsCriteria =
      calculatedProfit >= minProfit && calculatedRoi >= minRoi;

    return meetsCriteria
      ? {
          borderColor: "#87E8AD",
          backgroundColor: "#E9FBEF",
        }
      : {
          borderColor: "#A9444299",
          backgroundColor: "#F2DEDE",
        };
  };

  const getNextAveragePricingPeriod = (
    period: TAveragePricingPeriod,
  ): TAveragePricingPeriod => {
    if (period === "C") return "30";
    if (period === "30") return "90";
    if (period === "90") return "180";
    if (period === "180") return "All";
    if (period === "All") return "C";
    return "C";
  };

  const handleAveragePricingPeriodClick = () => {
    const nextPeriod = getNextAveragePricingPeriod(averagePricingPeriod);
    setAveragePricingPeriod(nextPeriod);
  };

  const getPeriodDisplayLabel = (period: TAveragePricingPeriod): string => {
    return period;
  };

  const fbaAverageValue =
    averagePricingPeriod === "C"
      ? (averages?.lowestFBA?.current?.average ??
        averages?.lowestFBA?.Current?.average)
      : averagePricingPeriod === "30"
        ? averages?.lowestFBA?.["30"]?.average
        : averagePricingPeriod === "90"
          ? averages?.lowestFBA?.["90"]?.average
          : averagePricingPeriod === "180"
            ? averages?.lowestFBA?.["180"]?.average
            : (averages?.lowestFBA?.All?.average ??
              averages?.lowestFBA?.all?.average);

  const bbAverageValue =
    averagePricingPeriod === "C"
      ? averages?.buyBox?.avgCurrent
      : averagePricingPeriod === "30"
        ? averages?.buyBox?.avg30
        : averagePricingPeriod === "90"
          ? averages?.buyBox?.avg90
          : averagePricingPeriod === "180"
            ? averages?.buyBox?.avg180
            : averages?.buyBox?.avgAll;

  const fbmAverageValue =
    averagePricingPeriod === "C"
      ? (averages?.lowestFBM?.current?.average ??
        averages?.lowestFBM?.Current?.average)
      : averagePricingPeriod === "30"
        ? averages?.lowestFBM?.["30"]?.average
        : averagePricingPeriod === "90"
          ? averages?.lowestFBM?.["90"]?.average
          : averagePricingPeriod === "180"
            ? averages?.lowestFBM?.["180"]?.average
            : (averages?.lowestFBM?.All?.average ??
              averages?.lowestFBM?.all?.average);

  let successAlerts: string[] = [],
    warningAlerts: string[] = [],
    dangerAlerts: string[] = [];
  alerts?.alertLevel?.forEach((alert: any) => {
    if (alert.level === "success") {
      successAlerts = successAlerts.concat(alert.message || []);
    } else if (alert.level === "warning") {
      warningAlerts = warningAlerts.concat(alert.message || []);
    } else if (alert.level === "danger") {
      dangerAlerts = dangerAlerts.concat(alert.message || []);
    }
  });

  return (
    <Box className={classes.root}>
      {!isConnected && (
        <Box
          sx={{
            backgroundColor: "#F2DEDE",
            border: "1px solid #A9444299",
          }}
        >
          <Typography
            sx={{
              color: "#A94442",
              fontSize: "10px",
              lineHeight: "15px",
              fontWeight: 500,
              paddingY: "1px",
              paddingX: "4px",
              borderRadius: "2px",
            }}
          >
            Connect Amazon Account for full functionality
          </Typography>
        </Box>
      )}
      <Box display={"grid"} gridTemplateColumns={"1fr 1fr"} gap="4px">
        <Panel title="sale price">
          <Box sx={{ py: "2px", width: "100%", height: "100%" }}>
            <DecimalInput
              prefix="$"
              value={profitCalculator.salePrice}
              onEditing={handlePriceChange}
              align="center"
            />
          </Box>
        </Panel>
        <Panel title="cost price">
          <Box sx={{ py: "2px", width: "100%", height: "100%" }}>
            <DecimalInput
              prefix="$"
              value={profitCalculator.costPrice}
              onEditing={handleCostChange}
              align="center"
            />
          </Box>
        </Panel>
      </Box>
      <Box display={"grid"} gridTemplateColumns={"1fr 1fr 1fr"} gap="4px">
        <Panel
          title="BSR"
          variant={
            bsrPercent >= minBsr && bsrPercent <= maxBsr ? "success" : "fail"
          }
        >
          {bsr > 0 ? (
            <Tooltip title={<>&#x1F44D; This meets your BSR criteria.</>} arrow>
              <Box>{getBsrDisplay(bsr, categoryProductCount)}</Box>
            </Tooltip>
          ) : (
            <Tooltip
              title={<>&#x26A0;&#xFE0F; We do not know BSR of this product.</>}
              arrow
            >
              <Box>Unknown</Box>
            </Tooltip>
          )}
        </Panel>
        <Panel
          title="Est. Sales"
          variant={toNumber(quickInfo.estSales) > 0 ? "info" : "warning"}
          // lock={isFreePlan}
          asin={asin}
          lookupId={lookupId}
        >
          <Tooltip
            title={
              toNumber(quickInfo.estSales) > 0 ? (
                <>
                  &#x2139;&#xFE0F; Estimated monthly sales{" "}
                  {formatNumber(quickInfo.estSales, { forcePlusSuffix: true })}.
                </>
              ) : (
                <>
                  &#x26A0;&#xFE0F; We do not have enough data points to estimate
                  the sales of this product.
                </>
              )
            }
            arrow
          >
            {toNumber(quickInfo.estSales) > 0 ? (
              <Box>
                {formatNumber(quickInfo.estSales, { forcePlusSuffix: true })}/mo
              </Box>
            ) : (
              <Box>Unknown</Box>
            )}
          </Tooltip>
        </Panel>
        <Panel
          title="Max Cost"
          variant={
            costPrice === 0
              ? "info"
              : !!maxCost && costPrice <= maxCost
                ? "success"
                : "fail"
          }
        >
          <Tooltip
            title={
              costPrice === 0 ? (
                <>&#x2139;&#xFE0F; Please enter cost price.</>
              ) : !!maxCost && costPrice <= maxCost ? (
                <>&#x1F44D; Your cost price meets your criteria.</>
              ) : (
                <>&#x1F44E; Your cost price does not meet your criteria.</>
              )
            }
            arrow
          >
            <Box>{formatCurrency(maxCost)}</Box>
          </Tooltip>
        </Panel>
      </Box>
      {/* <Box display="grid" gridTemplateColumns="1fr" gap="4px">
        <Panel
          title="Eligible"
          variant={
            quickInfo.eligibility?.eligible === "Yes"
              ? "success"
              : quickInfo.eligibility?.eligible === "No"
                ? "fail"
                : ["Not Connected", "Unknown"].includes(
                      quickInfo.eligibility?.eligible as string,
                    )
                  ? "warning"
                  : "default"
          }
        >
          <Tooltip
            title={
              quickInfo.eligibility?.hint ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: quickInfo.eligibility.hint,
                  }}
                ></div>
              ) : null
            }
            arrow
          >
            <Box sx={{ width: "100%", textAlign: "center" }}>
              {quickInfo.eligibility?.eligible || "-"}
            </Box>
          </Tooltip>
        </Panel>
      </Box> */}
      <Box
        display={"flex"}
        sx={{
          flexDirection: "row",
          flexWrap: "nowrap",
          justifyContent: "space-between",
          gap: "4px",
        }}
      >
        {showProfitInQuickInfo && (
          <Panel
            title="Profit"
            variant={
              profit >= toNumber(profitCalculator.minProfit)
                ? "success"
                : "fail"
            }
          >
            <Tooltip
              title={
                profit >= toNumber(profitCalculator.minProfit) ? (
                  <>&#x1F44D; This meets your minimum profit criteria.</>
                ) : (
                  <>
                    &#x1F44E; This does not meet your minimum profit criteria.
                  </>
                )
              }
              arrow
            >
              <Box>{formatCurrency(profit)}</Box>
            </Tooltip>
          </Panel>
        )}
        {showProfitMarginInQuickInfo && (
          <Panel
            title="Profit %"
            variant={
              profit >= toNumber(profitCalculator.minProfit)
                ? "success"
                : "fail"
            }
          >
            <Tooltip
              title={
                profit >= toNumber(profitCalculator.minProfit) ? (
                  <>&#x1F44D; This meets your minimum profit criteria.</>
                ) : (
                  <>
                    &#x1F44E; This does not meet your minimum profit criteria.
                  </>
                )
              }
              arrow
            >
              <Box>{formatPercent(margin)}</Box>
            </Tooltip>
          </Panel>
        )}
        {showRoiInQuickInfo && (
          <Panel
            title="ROI"
            variant={
              typeof roi === "undefined" || roi >= profitCalculator.minRoi
                ? "success"
                : "fail"
            }
          >
            <Tooltip
              title={
                typeof roi === "undefined" ||
                roi >= toNumber(profitCalculator.minRoi) ? (
                  <>&#x1F44D; This meets your minimum ROI criteria.</>
                ) : (
                  <>&#x1F44E; This does not meet your minimum ROI criteria.</>
                )
              }
              arrow
            >
              <Box>{formatPercent(roi)}</Box>
            </Tooltip>
          </Panel>
        )}
        {showBreakevenInQuickInfo && (
          <Panel
            title="Breakeven"
            variant={salePrice >= breakevenSalePrice ? "success" : "fail"}
          >
            <Tooltip
              title={
                salePrice >= breakevenSalePrice ? (
                  <>&#x1F44D; Your sale price is equal to or above breakeven.</>
                ) : (
                  <>&#x1F44E; Your sale price is below breakeven.</>
                )
              }
              arrow
            >
              <Box>{formatCurrency(breakevenSalePrice)}</Box>
            </Tooltip>
          </Panel>
        )}
      </Box>
      {showAveragePricingInQuickInfo && (
        <Panel
          title={
            <Box component="span">
              Average Pricing (
              <Box
                component="span"
                sx={{
                  textDecoration: "underline",
                  textDecorationStyle: "dotted",
                  textUnderlineOffset: "2px",
                }}
              >
                {getPeriodDisplayLabel(averagePricingPeriod)}
              </Box>
              )
            </Box>
          }
          onClickTitle={handleAveragePricingPeriodClick}
          isLoading={loadingAverages}
        >
          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr 1fr"
            gap="4px"
            py="2px"
            sx={{ width: "100%", textAlign: "center" }}
          >
            <Box
              sx={{
                borderRadius: "4px",
                border: `1px solid ${getAveragePriceBoxStyle(fbaAverageValue).borderColor}`,
                backgroundColor:
                  getAveragePriceBoxStyle(fbaAverageValue).backgroundColor,
                color: "#0F1111",
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: "15px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "24px",
                textAlign: "center",
                flexDirection: isSmallScreen ? "column" : "row",
                minWidth: 0,
              }}
            >
              <Box>FBA:&nbsp;</Box>
              <Box>
                <span style={{ color: getAveragePriceColor(fbaAverageValue) }}>
                  {fbaAverageValue == null || toNumber(fbaAverageValue) === 0
                    ? "$0"
                    : formatCurrency(fbaAverageValue)}
                </span>
              </Box>
            </Box>
            <Box
              sx={{
                borderRadius: "4px",
                border: `1px solid ${
                  getAveragePriceBoxStyle(fbmAverageValue).borderColor
                }`,
                backgroundColor:
                  getAveragePriceBoxStyle(fbmAverageValue).backgroundColor,
                color: "#0F1111",
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: "15px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "24px",
                textAlign: "center",
                flexDirection: isSmallScreen ? "column" : "row",
                minWidth: 0,
              }}
            >
              <Box>FBM:&nbsp;</Box>
              <Box>
                <span
                  style={{
                    color: getAveragePriceColor(fbmAverageValue),
                  }}
                >
                  {fbmAverageValue == null || toNumber(fbmAverageValue) === 0
                    ? "$0"
                    : formatCurrency(fbmAverageValue)}
                </span>
              </Box>
            </Box>
            <Box
              sx={{
                borderRadius: "4px",
                border: `1px solid ${getAveragePriceBoxStyle(bbAverageValue).borderColor}`,
                backgroundColor:
                  getAveragePriceBoxStyle(bbAverageValue).backgroundColor,
                color: "#0F1111",
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: "15px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "24px",
                textAlign: "center",
                flexDirection: isSmallScreen ? "column" : "row",
                minWidth: 0,
              }}
            >
              <Box>BB:&nbsp;</Box>
              <Box>
                <span style={{ color: getAveragePriceColor(bbAverageValue) }}>
                  {bbAverageValue == null || toNumber(bbAverageValue) === 0
                    ? "$0"
                    : formatCurrency(bbAverageValue)}
                </span>
              </Box>
            </Box>
          </Box>
        </Panel>
      )}

      {showOfferSummaryInQuickInfo && (
        <Box>
          <Box
            sx={{
              borderRadius: "4px",
              border: "1px solid #C9C9C9",
              bgcolor: "#F0F0F0",
              width: "100%",
              padding: "3px",
            }}
          >
            {/* Title and Total */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: "8px",
                px: "5px",
              }}
            >
              <Box
                sx={{
                  color: "#333333",
                  fontSize: "10px",
                  fontWeight: 600,
                  lineHeight: "15px",
                }}
              >
                OFFERS SUMMARY
              </Box>
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#333333",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  lineHeight: "17px",
                }}
              >
                <span style={{ textDecoration: "underline" }}>
                  Total:&nbsp;{offers?.length || 0}
                </span>
              </Box>
            </Box>
            {/* Three Boxes */}
            <Box
              display="grid"
              gridTemplateColumns="1fr 1fr 1fr"
              gap="8px"
              sx={{ width: "100%" }}
            >
              {/* AMZ Box */}
              <Box
                sx={{
                  borderRadius: "4px",
                  border: "1px solid #DAA520",
                  backgroundColor: "#FEF3C7",
                  color: "#B45309",
                  px: "8px",
                  py: "2px",
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  minHeight: "20px",
                }}
              >
                <Box sx={{ fontWeight: 500 }}>AMZ</Box>
                <Box sx={{ fontWeight: 700 }}>
                  <span>{hasAmazonOffer ? "YES" : "NO"}</span>
                </Box>
              </Box>

              {/* FBA Box */}
              <Box
                sx={{
                  borderRadius: "4px",
                  border: "1px solid #87E8AD",
                  backgroundColor: "#E9FBEF",
                  color: "#047647",
                  px: "8px",
                  py: "2px",
                  fontSize: "12px",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  minHeight: "20px",
                }}
              >
                <Box>FBA</Box>
                <Box>
                  <span>{fbaCount}</span>
                </Box>
              </Box>

              {/* FBM Box */}
              <Box
                sx={{
                  borderRadius: "4px",
                  border: "1px solid #93C5FD",
                  backgroundColor: "#EDF6FC",
                  color: "#2563EB",
                  px: "8px",
                  py: "2px",
                  fontSize: "12px",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  minHeight: "20px",
                }}
              >
                <Box>FBM</Box>
                <Box>
                  <span>{fbmCount}</span>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default memo(QuickInfo);
